import { useState, useEffect } from 'react';
import Sidebar from './components/Sidebar';
import { useNavigate, useLocation, Routes, Route } from 'react-router-dom';

import User from './user/user';
import UserAdmin from './user/user_admin';
import Clinic from './clinic/clinic';
import ClinicUserDashboard from './clinic/clinic_user_dashboard';
import Doctors from './doctors/doctors';
import DoctorsAdmin from './doctors/doctors_admin';
import Hospitals from './hospitals/hospital';
import Superadmin from './superadmin/superadmin';
import HospitalDetails from './hospitals/hospital_details';
import HospitalUserDashboard from './hospitals/hospital_user_dashboard';
import SuperRegister from './hospitals/super_hospital_register';
import SuperFetch from './hospitals/super_hospital_details';
import AddDoctors from './hospitals/add_doctors';
import DoctorsHistory from './hospitals/doctors_history';
import PackageDetails from './hospitals/package_details';
import AddonCredits from './hospitals/addon_credits';
import ClinicDetails from './clinic/clinic_details';
import SuperClinicRegister from './clinic/super_clinic_register';
import SuperClinicDetails from './clinic/super_clinic_details';
import ClinicAddDoctors from './clinic/add_doctors';
import ClinicDoctorsHistory from './clinic/doctors_history';
import ClinicPackageDetails from './clinic/package_details';
import ClinicAddonCredits from './clinic/addon_credits';
import AdminRegister from './superadmin/admin_register_page';
import SuperadminDashboard from './superadmin/superadmin_dashboard';
import Admin from './admin/admin';
import Setup from './superadmin/setup';  // ✅ Added Setup import
import Notifications from './superadmin/notifications';  // ✅ Added Notifications import
import Discounts from './superadmin/discounts';  // ✅ Added Discounts import
import SEO from './superadmin/SEO';  // ✅ Added SEO import
import SEOContent from './superadmin/SEO_Content';  // ✅ Added SEO Content import
import FAQ from './superadmin/faq';  // ✅ Added FAQ import
import FAQContent from './superadmin/faq_content';  // ✅ Added FAQ Content import
import Blog from './superadmin/blog';  // ✅ Added Blog import
import BlogContent from './superadmin/blog_content';  // ✅ Added Blog Content import
import HospitalsDashboard from './hospitals/hospitals_dashboard';
import ClinicDashboard from './clinic/clinic_dashboard';
import PackageAdmin from './admin/package_admin';
import CreateDepartment from './departments/create_department';


export default function Dashboard() {
  const [role, setRole] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();

  // Function to navigate to a section
  const handleNavigate = (section, options = {}) => {
    console.log('🔍 handleNavigate called with section:', section);
    console.log('🔍 handleNavigate options:', options);
    
    // Handle blog navigation
    if (section === 'blog-content') {
      const { mode, postId } = options;
      if (mode === 'create') {
        console.log('🚀 Navigating to: /blog_create');
        navigate('/blog_create');
      } else if (mode === 'edit' && postId) {
        console.log('🚀 Navigating to: /blog_edit/' + postId);
        navigate(`/blog_edit/${postId}`);
      } else if (mode === 'view' && postId) {
        console.log('🚀 Navigating to: /blog_view/' + postId);
        navigate(`/blog_view/${postId}`);
      }
      return;
    }
    
    // Handle blog categories (placeholder for future implementation)
    if (section === 'blog-categories') {
      console.log('🚀 Blog categories navigation - not implemented yet');
      // For now, just stay on blog page
      navigate('/blog');
      return;
    }
    
    // Handle other navigation
    console.log('🚀 Navigating to:', `/${section}`);
    navigate(`/${section}`);
  };

  const handleBack = () => {
    // Navigate back to parent dashboard based on current path and role
    const path = location.pathname;
    
    // FAQ and Blog should go back to SEO page
    if (path.includes('faq') || path.includes('blog')) {
      navigate('/seo');
    } else if (path.includes('setup') || path.includes('notifications') || path.includes('discounts') || path.includes('seo')) {
      // From setup, notifications, discounts, or seo page, go back to appropriate dashboard
      if (role === 5) {
        navigate('/admin');
      } else {
        navigate('/superadmin_dashboard');
      }
    } else if (path.includes('hospital')) {
      // For hospital users (role 4), go to their dashboard
      if (role === 4) {
        navigate('/hospitals');
      } else {
        navigate('/hospitals_dashboard');
      }
    } else if (path.includes('clinic')) {
      // For clinic users (role 3), go to their dashboard
      if (role === 3) {
        navigate('/clinic');
      } else {
        navigate('/clinic_dashboard');
      }
    } else if (path.includes('admin')) {
      if (role === 5) {
        navigate('/admin');
      } else {
        navigate('/superadmin_dashboard');
      }
    }
  };

  // Custom back handler for SEO content pages
  const handleSeoBack = () => {
    navigate('/seo');
  };

  // Custom back handler for FAQ content pages
  const handleFaqBack = () => {
    navigate('/faq');
  };

  // Custom back handler for Blog content pages
  const handleBlogBack = () => {
    navigate('/blog');
  };

  const roleAccess = {
    6: ['superadmin', 'user', 'clinic', 'doctors', 'hospitals', 'package', 'departments', 'seo', 'faq', 'blog'],
    5: ['user', 'clinic', 'doctors', 'hospitals', 'package', 'departments', 'seo', 'faq', 'blog'],
    4: ['hospitals'],
    3: ['clinic'],
    2: ['doctors'],
    1: ['user'],
  };
  
  const allowedSections = roleAccess[role] || [];

  useEffect(() => {
    const storedRole = localStorage.getItem('userRole');
    const token = localStorage.getItem('accessToken');
    
    if (storedRole && token) {
      const parsedRole = parseInt(storedRole);
      setRole(parsedRole);
      
      // Redirect to default section if on root or /dashboard
      if (location.pathname === '/' || location.pathname === '' || location.pathname === '/dashboard') {
        // Role-based default pages
        if (parsedRole === 6) {
          // Superadmin → Show superadmin dashboard
          navigate('/superadmin_dashboard', { replace: true });
        } else if (parsedRole === 5) {
          // Admin → Show admin greeting page
          navigate('/admin', { replace: true });
        } else {
          // Other roles → Use first allowed section
          const defaultSection = roleAccess[parsedRole]?.[0] || null;
          if (defaultSection) {
            navigate(`/${defaultSection}`, { replace: true });
          }
        }
      }
    } else {
      // Use replace to prevent going forward to dashboard
      navigate('/', { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Prevent browser back button from going to login page
  useEffect(() => {
    const handlePopState = (e) => {
      const token = localStorage.getItem('accessToken');
      if (token) {
        // If logged in, prevent going back
        window.history.pushState(null, '', window.location.pathname);
      }
    };

    // Push initial state
    window.history.pushState(null, '', window.location.pathname);
    window.addEventListener('popstate', handlePopState);

    return () => {
      window.removeEventListener('popstate', handlePopState);
    };
  }, []);



  return (
    <div className="min-h-screen bg-slate-50">
      <Sidebar />
      
      {/* Top Header Bar */}
      {/* <div className="fixed top-0 left-64 right-0 h-16 bg-white border-b border-slate-200 z-30 flex items-center justify-between px-6">
        <div>
          <h2 className="text-lg font-semibold text-slate-900">
            {location.pathname.split('/').pop().replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) || 'Dashboard'}
          </h2>
          <p className="text-xs text-slate-600">Welcome back</p>
        </div>
      </div> */}
      
      <main className="ml-64 pt-0 min-h-screen">
        <div className="p-6">
              <Routes>
                {/* Superadmin Routes */}
                {allowedSections.includes('superadmin') && (
                  <>
                    <Route path="/superadmin" element={<Superadmin />} />
                    <Route path="/superadmin_dashboard" element={<SuperadminDashboard onNavigate={handleNavigate} />} />
                    <Route path="/add_admin" element={<AdminRegister onBack={handleBack} />} />
                    <Route path="/setup" element={<Setup onBack={handleBack} />} />
                    <Route path="/notifications" element={<Notifications onBack={handleBack} />} />
                    <Route path="/discounts" element={<Discounts onBack={handleBack} />} />
                    <Route path="/seo" element={<SEO onBack={handleBack} onNavigate={handleNavigate} />} />
                    <Route path="/seo_create" element={<SEOContent onBack={handleSeoBack} mode="create" />} />
                    <Route path="/seo_edit/:seoId" element={<SEOContent onBack={handleSeoBack} mode="edit" />} />
                    <Route path="/seo_view/:seoId" element={<SEOContent onBack={handleSeoBack} mode="view" />} />
                    <Route path="/faq" element={<FAQ onBack={handleBack} onNavigate={handleNavigate} />} />
                    <Route path="/faq_create" element={<FAQContent onBack={handleFaqBack} mode="create" />} />
                    <Route path="/faq_edit/:faqId" element={<FAQContent onBack={handleFaqBack} mode="edit" />} />
                    <Route path="/faq_view/:faqId" element={<FAQContent onBack={handleFaqBack} mode="view" />} />
                    <Route path="/blog" element={<Blog onBack={handleBack} onNavigate={handleNavigate} />} />
                    <Route path="/blog_create" element={<BlogContent onBack={handleBlogBack} mode="create" />} />
                    <Route path="/blog_edit/:postId" element={<BlogContent onBack={handleBlogBack} mode="edit" />} />
                    <Route path="/blog_view/:postId" element={<BlogContent onBack={handleBlogBack} mode="view" />} />
                  </>
                )}

                {/* Admin Dashboard Route (for role 5) */}
                {role === 5 && (
                  <>
                    <Route path="/admin" element={<Admin onNavigate={handleNavigate} />} />
                    <Route path="/setup" element={<Setup onBack={handleBack} />} />
                    <Route path="/notifications" element={<Notifications onBack={handleBack} />} />
                    <Route path="/discounts" element={<Discounts onBack={handleBack} />} />
                  </>
                )}

                {/* SEO Routes (for role 5 and 6) */}
                {allowedSections.includes('seo') && (
                  <>
                    <Route path="/seo" element={<SEO onBack={handleBack} onNavigate={handleNavigate} />} />
                    <Route path="/seo_create" element={<SEOContent onBack={handleSeoBack} mode="create" />} />
                    <Route path="/seo_edit/:seoId" element={<SEOContent onBack={handleSeoBack} mode="edit" />} />
                    <Route path="/seo_view/:seoId" element={<SEOContent onBack={handleSeoBack} mode="view" />} />
                  </>
                )}

                {/* FAQ Routes (for role 5 and 6) */}
                {allowedSections.includes('faq') && (
                  <>
                    <Route path="/faq" element={<FAQ onBack={handleBack} onNavigate={handleNavigate} />} />
                    <Route path="/faq_create" element={<FAQContent onBack={handleFaqBack} mode="create" />} />
                    <Route path="/faq_edit/:faqId" element={<FAQContent onBack={handleFaqBack} mode="edit" />} />
                    <Route path="/faq_view/:faqId" element={<FAQContent onBack={handleFaqBack} mode="view" />} />
                  </>
                )}

                {/* Blog Routes (for role 5 and 6) */}
                {allowedSections.includes('blog') && (
                  <>
                    <Route path="/blog" element={<Blog onBack={handleBack} onNavigate={handleNavigate} />} />
                    <Route path="/blog_create" element={<BlogContent onBack={handleBlogBack} mode="create" />} />
                    <Route path="/blog_edit/:postId" element={<BlogContent onBack={handleBlogBack} mode="edit" />} />
                    <Route path="/blog_view/:postId" element={<BlogContent onBack={handleBlogBack} mode="view" />} />
                  </>
                )}

                {/* User Routes */}
                {allowedSections.includes('user') && (
                  <>
                    <Route path="/user" element={<User />} />
                    <Route path="/Super_user_details" element={<UserAdmin />} />
                  </>
                )}

                {/* Doctors Routes */}
                {allowedSections.includes('doctors') && (
                  <>
                    <Route path="/doctors" element={<Doctors />} />
                    <Route path="/doctor_analyze" element={<DoctorsAdmin />} />
                  </>
                )}

                {/* Hospital Routes */}
                {allowedSections.includes('hospitals') && (
                  <>
                    {/* For Hospital Users (role 4) - Show their own dashboard */}
                    {role === 4 && <Route path="/hospitals" element={<HospitalUserDashboard />} />}
                    
                    {/* For Superadmin/Admin (role 5/6) - Show management dashboard */}
                    {(role === 5 || role === 6) && (
                      <>
                        <Route path="/hospitals" element={<Hospitals />} />
                        <Route path="/hospitals_dashboard" element={<HospitalsDashboard onNavigate={handleNavigate} />} />
                        <Route path="/hospitals_dashboard/add_doctors" element={<AddDoctors onBack={handleBack} />} />
                        <Route path="/super_hospital" element={<SuperRegister onBack={handleBack} />} />
                        <Route path="/super_hospital_details" element={<SuperFetch onBack={handleBack} />} />
                      </>
                    )}
                    
                    {/* Common routes for all hospital-related roles */}
                    <Route path="/hospital_details" element={<HospitalDetails />} />
                    <Route path="/add_doctors" element={<AddDoctors onBack={handleBack} />} />
                    <Route path="/doctors_history" element={<DoctorsHistory />} />
                    <Route path="/package_details" element={<PackageDetails />} />
                    <Route path="/addon_credits" element={<AddonCredits />} />
                  </>
                )}

                {/* Clinic Routes */}
                {allowedSections.includes('clinic') && (
                  <>
                    {/* For Clinic Users (role 3) - Show their own dashboard */}
                    {role === 3 && <Route path="/clinic" element={<ClinicUserDashboard />} />}
                    
                    {/* For Superadmin/Admin (role 5/6) - Show management dashboard */}
                    {(role === 5 || role === 6) && (
                      <>
                        <Route path="/clinic" element={<Clinic />} />
                        <Route path="/clinic_dashboard" element={<ClinicDashboard onNavigate={handleNavigate} />} />
                        <Route path="/clinic_dashboard/add_doctors" element={<ClinicAddDoctors onBack={handleBack} />} />
                        <Route path="/super_clinic" element={<SuperClinicRegister onBack={handleBack} />} />
                        <Route path="/super_clinic_details" element={<SuperClinicDetails onBack={handleBack} />} />
                      </>
                    )}
                    
                    {/* Common routes for all clinic-related roles */}
                    <Route path="/clinic_details" element={<ClinicDetails />} />
                    <Route path="/clinic_package_details" element={<ClinicPackageDetails />} />
                    <Route path="/clinic_addon_credits" element={<ClinicAddonCredits />} />
                    <Route path="/clinic_doctors_history" element={<ClinicDoctorsHistory />} />
                    <Route path="/clinic_dashboard/add_doctors" element={<ClinicAddDoctors onBack={handleBack} />} />
                  </>
                )}

                {/* Package & Department Routes */}
                {allowedSections.includes('package') && (
                  <Route path="/create_package" element={<PackageAdmin />} />
                )}
                {allowedSections.includes('departments') && (
                  <Route path="/create_department" element={<CreateDepartment onBack={handleBack} />} />
                )}
              </Routes>
        </div>
      </main>
    </div>
  );
}